export const PRO_PLAN_TYPE = 'AXONIQ_CONSOLE_PRO'

export type SubscriptionStatus =
    'NO_PENDING_CHANGE' |
    'PENDING_ACTIVATION' |
    'PENDING_CHANGE' |
    'PENDING_CANCELLATION'

export type PlanType = 'AXONIQ_CONSOLE_PRO'

export interface BillingAccountStatus {
    accountId: string
    active: boolean
    balance: number
    components: SubscriptionComponentStatus[]
    dailyComponentUpdateLimit: number
    currency: Currency
    paymentMethod: PaymentMethod
    invoiceBased: boolean
    pendingInvoices: PendingInvoice[]
}

export interface PendingInvoice {
    invoiceId: string
    invoiceUrl: string
    total: number
    dueDate: number
    pastDue: boolean
    leniencyDate: string
}

export interface SubscriptionComponent {
    type: string
    id: string
}

export interface SubscriptionComponentStatus {
    componentId: SubscriptionComponent
    plans: {
        [key: string]: number
    },
    subscriptionStatus: SubscriptionStatus,
    dailyUpdates: number,
    dailyUpdatesLimit: number,
    updatesBlocked: boolean
}

export interface Pricing {
    prices: Price[]
}

export interface Price {
    currency: Currency
    prices: {
        [key: string]: PricingTier[]
    }

}

export interface PricingTier {
    pricePerUnit: number
    upTo: number
}

export type Currency = "USD" | "EUR"

export type PaymentMethod =
    "CREDIT_CARD_USD"
    | "CREDIT_CARD_EUR"
    | "PAYPAL_USD"
    | "PAYPAL_EUR"
    | "IDEAL_EUR"
    | "SEPA_DEBIT_EUR"
    | "INVOICE"


// These are the tax id types that are available in Stripe
export const taxIdTypes = [
    {"value": "ad_nrt", title: "Andorra NRT"},
    {"value": "ae_trn", title: "United Arab Emirates TRN"},
    {"value": "al_tin", title: "Albania TIN"},
    {"value": "am_tin", title: "Armenia TIN"},
    {"value": "ao_tin", title: "Angola TIN"},
    {"value": "ar_cuit", title: "Argentina CUIT"},
    {"value": "au_abn", title: "Australia ABN"},
    {"value": "au_arn", title: "Australia ARN"},
    {"value": "ba_tin", title: "Bosnia and Herzegovina TIN"},
    {"value": "bb_tin", title: "Barbados TIN"},
    {"value": "bg_uic", title: "Bulgaria UIC"},
    {"value": "bh_vat", title: "Bahrain VAT"},
    {"value": "bo_tin", title: "Bolivia TIN"},
    {"value": "br_cnpj", title: "Brazil CNPJ"},
    {"value": "br_cpf", title: "Brazil CPF"},
    {"value": "bs_tin", title: "Bahamas TIN"},
    {"value": "by_tin", title: "Belarus TIN"},
    {"value": "ca_bn", title: "Canada BN"},
    {"value": "ca_gst_hst", title: "Canada GST/HST"},
    {"value": "ca_pst_bc", title: "Canada PST (BC)"},
    {"value": "ca_pst_mb", title: "Canada PST (MB)"},
    {"value": "ca_pst_sk", title: "Canada PST (SK)"},
    {"value": "ca_qst", title: "Canada QST"},
    {"value": "cd_nif", title: "Congo NIF"},
    {"value": "ch_uid", title: "Switzerland UID"},
    {"value": "ch_vat", title: "Switzerland VAT"},
    {"value": "cl_tin", title: "Chile TIN"},
    {"value": "cn_tin", title: "China TIN"},
    {"value": "co_nit", title: "Colombia NIT"},
    {"value": "cr_tin", title: "Costa Rica TIN"},
    {"value": "de_stn", title: "Germany STN"},
    {"value": "do_rcn", title: "Dominican Republic RCN"},
    {"value": "ec_ruc", title: "Ecuador RUC"},
    {"value": "eg_tin", title: "Egypt TIN"},
    {"value": "es_cif", title: "Spain CIF"},
    {"value": "eu_vat", title: "EU VAT"},
    {"value": "gb_vat", title: "United Kingdom VAT"},
    {"value": "ge_vat", title: "Georgia VAT"},
    {"value": "gn_nif", title: "Guinea NIF"},
    {"value": "hk_br", title: "Hong Kong BR"},
    {"value": "hr_oib", title: "Croatia OIB"},
    {"value": "hu_tin", title: "Hungary TIN"},
    {"value": "id_npwp", title: "Indonesia NPWP"},
    {"value": "il_vat", title: "Israel VAT"},
    {"value": "in_gst", title: "India GST"},
    {"value": "is_vat", title: "Iceland VAT"},
    {"value": "jp_cn", title: "Japan CN"},
    {"value": "jp_rn", title: "Japan RN"},
    {"value": "jp_trn", title: "Japan TRN"},
    {"value": "ke_pin", title: "Kenya PIN"},
    {"value": "kh_tin", title: "Cambodia TIN"},
    {"value": "kr_brn", title: "South Korea BRN"},
    {"value": "kz_bin", title: "Kazakhstan BIN"},
    {"value": "li_uid", title: "Liechtenstein UID"},
    {"value": "li_vat", title: "Liechtenstein VAT"},
    {"value": "ma_vat", title: "Morocco VAT"},
    {"value": "md_vat", title: "Moldova VAT"},
    {"value": "me_pib", title: "Montenegro PIB"},
    {"value": "mk_vat", title: "North Macedonia VAT"},
    {"value": "mr_nif", title: "Mauritania NIF"},
    {"value": "mx_rfc", title: "Mexico RFC"},
    {"value": "my_frp", title: "Malaysia FRP"},
    {"value": "my_itn", title: "Malaysia ITN"},
    {"value": "my_sst", title: "Malaysia SST"},
    {"value": "ng_tin", title: "Nigeria TIN"},
    {"value": "no_vat", title: "Norway VAT"},
    {"value": "no_voec", title: "Norway VOEC"},
    {"value": "np_pan", title: "Nepal PAN"},
    {"value": "nz_gst", title: "New Zealand GST"},
    {"value": "om_vat", title: "Oman VAT"},
    {"value": "pe_ruc", title: "Peru RUC"},
    {"value": "ph_tin", title: "Philippines TIN"},
    {"value": "ro_tin", title: "Romania TIN"},
    {"value": "rs_pib", title: "Serbia PIB"},
    {"value": "ru_inn", title: "Russia INN"},
    {"value": "ru_kpp", title: "Russia KPP"},
    {"value": "sa_vat", title: "Saudi Arabia VAT"},
    {"value": "sg_gst", title: "Singapore GST"},
    {"value": "sg_uen", title: "Singapore UEN"},
    {"value": "si_tin", title: "Slovenia TIN"},
    {"value": "sn_ninea", title: "Senegal NINEA"},
    {"value": "sr_fin", title: "Suriname FIN"},
    {"value": "sv_nit", title: "El Salvador NIT"},
    {"value": "th_vat", title: "Thailand VAT"},
    {"value": "tj_tin", title: "Tajikistan TIN"},
    {"value": "tr_tin", title: "Turkey TIN"},
    {"value": "tw_vat", title: "Taiwan VAT"},
    {"value": "tz_vat", title: "Tanzania VAT"},
    {"value": "ua_vat", title: "Ukraine VAT"},
    {"value": "ug_tin", title: "Uganda TIN"},
    {"value": "us_ein", title: "United States EIN"},
    {"value": "uy_ruc", title: "Uruguay RUC"},
    {"value": "uz_tin", title: "Uzbekistan TIN"},
    {"value": "uz_vat", title: "Uzbekistan VAT"},
    {"value": "ve_rif", title: "Venezuela RIF"},
    {"value": "vn_tin", title: "Vietnam TIN"},
    {"value": "za_vat", title: "South Africa VAT"},
    {"value": "zm_tin", title: "Zambia TIN"},
    {"value": "zw_tin", title: "Zimbabwe TIN"}
]